import WarningIcon from '@mui/icons-material/Warning';
import { Box, Button, Stack, Typography } from '@mui/material';
import tokens from '@verifime/design-tokens';
import FlexPaper from '../FlexPaper';

import blurryImage from './blurry.jpg';
import correctImage from './correct.jpg';
import cutoffImage from './cutoff.jpg';
import flashlightImage from './flashlight.jpg';
import handCoveredImage from './hand covered.jpg';
import multipleIDImage from './multiple ID.jpg';
import perspectiveImage from './perspective.jpg';

interface IdentityDocumentPhotoGuideProps {
  dataTestId?: string;
  compact?: boolean;
  onUploadAgain?: () => void;
}

/**
 * Component that displays an error message when ID image reading fails,
 * showing examples of acceptable and unacceptable ID images.
 */
export default function IdentityDocumentPhotoGuide({
  dataTestId,
  compact,
  onUploadAgain,
}: Readonly<IdentityDocumentPhotoGuideProps>) {
  return (
    <FlexPaper
      sx={{
        borderRadius: tokens.borderRadiusSm,
        padding: tokens.spacingSm,
        gap: tokens.spacingBase,
      }}
      data-component="PhotoGuide"
      data-testid={dataTestId}
    >
      {!compact && (
        <>
          <Stack direction="row" gap={tokens.spacingSm} data-component="header">
            <WarningIcon color="error" />
            <Typography variant="h5">We had an issue reading your image</Typography>
          </Stack>
          {/* TODO: Change to tokens once we have a design token for this */}
          <Typography variant="body1" sx={{ lineHeight: '150%', letterSpacing: '0.15px' }}>
            We weren&apos;t able to read your ID properly. This could be due to image quality or
            blocked fields. Please reupload your ID or fill in the details manually, then continue.
          </Typography>
        </>
      )}

      <Stack gap={tokens.spacingSm}>
        <Typography variant="h6">Accepted ID Image</Typography>
        <Stack direction="row" gap={tokens.spacingSm} alignItems="center" justifyContent="center">
          <Box>
            <img
              src={correctImage.src}
              alt="Example of correctly captured ID"
              style={{
                width: '100%',
                height: 'auto',
              }}
            />
          </Box>
          {/* TODO: Change to tokens once we have a design token for this */}
          <Typography variant="body1" sx={{ lineHeight: '150%', letterSpacing: '0.15px' }}>
            Place your ID on a clean, flat surface and take a photo. Make sure all details are
            clearly visible.
          </Typography>
        </Stack>
      </Stack>

      <Stack gap={tokens.spacingSm}>
        <Typography variant="h6">Unacceptable ID Images</Typography>
        <Stack direction="row" gap={tokens.spacingSm}>
          <Box>
            <img
              src={blurryImage.src}
              alt={`Blurry: Image is not clear or in focus`}
              style={{
                width: '100%',
                height: 'auto',
              }}
            />
          </Box>
          <Box>
            <img
              src={cutoffImage.src}
              alt={`Cut off: Parts of the ID are not visible`}
              style={{
                width: '100%',
                height: 'auto',
              }}
            />
          </Box>
          <Box>
            <img
              src={handCoveredImage.src}
              alt={`Hand covered: ID partially covered by hand`}
              style={{
                width: '100%',
                height: 'auto',
              }}
            />
          </Box>
        </Stack>
        <Stack direction="row" gap={tokens.spacingSm}>
          <Box>
            <img
              src={perspectiveImage.src}
              alt={`Angled: ID taken at an angle instead of flat`}
              style={{
                width: '100%',
                height: 'auto',
              }}
            />
          </Box>
          <Box>
            <img
              src={flashlightImage.src}
              alt={`Flash glare: Strong flash reflection on ID`}
              style={{
                width: '100%',
                height: 'auto',
              }}
            />
          </Box>
          <Box>
            <img
              src={multipleIDImage.src}
              alt={`Multiple IDs: More than one ID in the image`}
              style={{
                width: '100%',
                height: 'auto',
              }}
            />
          </Box>
        </Stack>
      </Stack>

      {/* TODO: Change to tokens once we have a design token for this */}
      <Typography variant="body1" sx={{ lineHeight: '150%', letterSpacing: '0.15px' }}>
        Photos won&apos;t be accepted if they are blurry, cut off, covered by your hand, taken at an
        angle, have strong flash glare, or include more than one ID.
      </Typography>
      {!compact && (
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={onUploadAgain}
            data-action="upload-again"
          >
            Upload Again
          </Button>
        </Box>
      )}
    </FlexPaper>
  );
}
